// components/mobile/CardList.jsx
// Pola generik "tabel → kartu" untuk layar sempit.
// Setiap item jadi kartu: judul + pill status + subbaris muted. Tap = onOpen(item)
// (biasanya membuka DetailSheet). Mendukung multi-select via long-press area kiri.
//
// Props:
//   items        : array data (sudah difilter/sort oleh halaman)
//   keyOf        : (item) => string unik
//   title        : (item) => node — kolom identitas utama
//   badge        : (item) => { label, tone: 'emerald'|'rose'|'amber'|'blue'|'slate', Icon? } | null
//   subtitle     : (item) => node — 2–3 kolom sekunder, dipisah " · "
//   onOpen       : (item) => void — tap kartu
//   selected?    : Set<string> — kalau diberikan, mode seleksi aktif
//   onToggle?    : (key) => void
//   emptyText?   : string
import React from 'react';

const TONE = {
  emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  rose:    'bg-rose-50 text-rose-600 border-rose-200',
  amber:   'bg-amber-50 text-amber-700 border-amber-200',
  blue:    'bg-blue-50 text-blue-700 border-blue-200',
  slate:   'bg-slate-100 text-slate-500 border-slate-200',
};

export default function CardList({
  items, keyOf, title, badge, subtitle, onOpen,
  selected, onToggle, emptyText = '—',
}) {
  if (!items.length) {
    return <div className="px-5 py-10 text-center text-slate-400 text-sm">{emptyText}</div>;
  }
  const selectable = selected instanceof Set && typeof onToggle === 'function';
  return (
    <ul className="divide-y divide-slate-100">
      {items.map((item) => {
        const k = keyOf(item);
        const sel = selectable && selected.has(k);
        const b = badge ? badge(item) : null;
        return (
          <li key={k} className={sel ? 'bg-blue-50/60' : 'bg-white'}>
            <div className="flex items-stretch">
              {selectable && (
                <button
                  onClick={(e) => { e.stopPropagation(); onToggle(k); }}
                  aria-label={sel ? 'Deselect' : 'Select'}
                  className="pl-3 pr-2 flex items-center"
                >
                  <span className={`w-5 h-5 rounded border flex items-center justify-center text-[11px]
                    ${sel ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 text-transparent'}`}>✓</span>
                </button>
              )}
              <button
                onClick={() => onOpen && onOpen(item)}
                className="flex-1 text-left px-3 py-3 min-h-[56px] active:bg-slate-50"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-slate-700 text-sm truncate">{title(item)}</span>
                  {b && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border shrink-0 ${TONE[b.tone] || TONE.slate}`}>
                      {b.Icon && <b.Icon style={{ width: 11, height: 11 }} />}{b.label}
                    </span>
                  )}
                </div>
                {subtitle && (
                  <p className="mt-0.5 text-xs text-slate-400 truncate">{subtitle(item)}</p>
                )}
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
